import http from '@/lib/http'
import { useQuery } from '@tanstack/react-query'

/** API response shape for a single investigation list item. */
export type APIInvestigationListItem = {
  orderItemHash: string
  orderItemName: string | null
  orderItemStatus: string | null
  productHash: string | null
  productName: string | null
  createdAt: string | null
  updatedAt: string | null
}

/** API response shape for the investigations list endpoint. */
type GetInvestigationsAPIResponse = {
  investigations: APIInvestigationListItem[]
}

/**
 * Fetch investigations scoped to a specific patient hash.
 */
export const fetchInvestigationsByPatientHash = async (patientHash: string): Promise<APIInvestigationListItem[]> => {
  const { investigations } = (await http.get('/investigations', {
    params: { patientHash }
  })) as GetInvestigationsAPIResponse

  return investigations
}

/**
 * Fetch investigations scoped to a specific order hash.
 */
export const fetchInvestigationsByOrderHash = async (orderHash: string): Promise<APIInvestigationListItem[]> => {
  const { investigations } = (await http.get('/investigations', {
    params: { orderHash }
  })) as GetInvestigationsAPIResponse

  return investigations
}

/**
 * React Query hook to fetch investigations by patient hash.
 */
export const useInvestigationsByPatientHashQuery = (patientHash: string | undefined) => {
  return useQuery({
    queryKey: ['investigations', 'patient', patientHash],
    queryFn: () => fetchInvestigationsByPatientHash(patientHash!),
    enabled: !!patientHash
  })
}

/**
 * React Query hook to fetch investigations by order hash.
 */
export const useInvestigationsByOrderHashQuery = (orderHash: string | undefined) => {
  return useQuery({
    queryKey: ['investigations', 'order', orderHash],
    queryFn: () => fetchInvestigationsByOrderHash(orderHash!),
    enabled: !!orderHash
  })
}
